import { useEffect, useRef, useState } from "react";
import { getEnrollment } from "@/lib/face/face-enrollment-store";
import {
  getFaceWatchStream,
  getLastFaceObservation,
  subscribeFaceObservations,
  subscribeFaceWatchState,
} from "@/lib/face/face-watcher";
import { cn } from "@/lib/utils";

/**
 * Live view of what face unlock is actually seeing: the camera, with a box round each detected
 * face labelled with who it thinks that is.
 *
 * This exists to make failure legible. "We don't recognize you" is infuriating with no reason
 * attached; seeing your own face boxed in red at the edge of frame, or no box at all, tells you
 * in one glance whether to move closer, turn on a light, or press "It's me".
 *
 * Attaches the watcher's EXISTING stream — it never opens the camera itself, so it is inert
 * (renders nothing) whenever face unlock isn't already running.
 */
export function FaceCameraPreview({ className, mirrored = true }) {
  const videoRef = useRef(null);
  const [faces, setFaces] = useState(() => getLastFaceObservation()?.faces ?? []);
  // The camera decides its own resolution (constraints are `ideal`, not `exact`), and boxRaw is
  // normalized to whatever it chose. Tracking the real ratio keeps the box overlay locked to the
  // pixels it describes — assuming 4:3 would silently skew every box on a 16:9 webcam.
  const [aspectRatio, setAspectRatio] = useState(4 / 3);
  const [hasVideo, setHasVideo] = useState(false);
  // Held in state, not read inline: this component usually mounts while the camera is still
  // opening, and a plain getter would render null once and never re-render when the stream
  // arrived.
  const [stream, setStream] = useState(() => getFaceWatchStream());

  // Re-attach on every watch-state change, not just on mount: the stream is a fresh object
  // after any stop/start.
  useEffect(() => {
    const sync = () => setStream(getFaceWatchStream());
    sync();
    return subscribeFaceWatchState(sync);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    // Autoplay can reject if the element is torn down mid-play; nothing to recover, the next
    // state change re-attaches.
    void video.play().catch(() => {});
  }, [stream]);

  useEffect(() => subscribeFaceObservations((observation) => setFaces(observation.faces)), []);

  if (!stream) return null;

  return (
    <div
      className={cn("relative overflow-hidden rounded-lg border border-border bg-muted", className)}
      style={{ aspectRatio }}
    >
      <video
        ref={videoRef}
        muted
        playsInline
        aria-hidden
        // Mirrored by default: an un-mirrored selfie view makes people correct their position
        // the wrong way. The boxes below are flipped to match.
        className={cn("h-full w-full object-cover", mirrored && "scale-x-[-1]")}
        onLoadedMetadata={(event) => {
          const { videoWidth, videoHeight } = event.currentTarget;
          if (videoWidth > 0 && videoHeight > 0) setAspectRatio(videoWidth / videoHeight);
          setHasVideo(true);
        }}
      />

      {hasVideo
        ? faces.map((face, index) => {
            const [x, y, width, height] = face.boxRaw || [0, 0, 0, 0];
            if (!(width > 0) || !(height > 0)) return null;
            const enrollment = face.userId ? getEnrollment(face.userId) : null;
            const recognized = Boolean(enrollment);
            // The box is flipped in the same axis as the video, so it stays on the face; the
            // LABEL is not, or the name would read backwards.
            const left = mirrored ? 1 - x - width : x;
            return (
              <div
                key={face.userId || `face-${index}`}
                className={cn(
                  "pointer-events-none absolute rounded-md border-2 transition-[border-color] duration-300",
                  recognized ? "border-emerald-400" : "border-destructive",
                )}
                style={{
                  left: `${left * 100}%`,
                  top: `${y * 100}%`,
                  width: `${width * 100}%`,
                  height: `${height * 100}%`,
                }}
              >
                {/* Deliberately allowed to overflow the box rather than truncate: the box is
                    only as wide as someone's face, and "Not recogni…" tells them nothing. */}
                <span
                  className={cn(
                    "absolute -bottom-0.5 left-1/2 -translate-x-1/2 translate-y-full whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium leading-tight",
                    recognized
                      ? "bg-emerald-400 text-emerald-950"
                      : "bg-destructive text-destructive-foreground",
                  )}
                >
                  {recognized ? enrollment.displayName : "Not recognized"}
                </span>
              </div>
            );
          })
        : null}
    </div>
  );
}
